import React, { Fragment } from "react";
import { lazyWithRetry } from "../../core/lazy_with_retry";

import { useDailyLocationGalleryFeatureFlag } from "../../core/splitio";
import { GeneralInformation } from "../template_offering_details_base";
import { OfferingDetailsMultiPass } from "../template_offering_details_multipass";
import { Text } from "../text";
import { TextLink } from "../text_link";
import { useWeWorkAnalytics } from "./wework_analytics";
import { useWeWorkPartnerConfig } from "./wework_partner_config";

const Theme = lazyWithRetry(() => import("./wework_theme"));

export const faqsEN = [
  {
    question: "What does Multi-Day Access give me permission to?",
    answer:
      "The Multi-Day Pass grants you entry to any selected WeWork location on the dates you reserved in advance. During your reservation, your access is granted during business hours (9 AM - 5 PM), Sunday – Thursday. Your open workspace booking will give you permission to work in the hot desk and/or common areas of the specific location. You’ll have access to a seat with a back, a flat work surface, and a power source within reach.",
  },
  {
    question:
      "How do I start using the Multi-Day Access once arriving at the WeWork location?",
    answer:
      "When you first arrive at the building, please check-in with a member of our Community Team at the front desk. According to the building you reserved, you will get set up with a keycard – just don’t forget your ID, the Community Team will need to check that first!",
  },
  {
    question:
      "Anything specific I need to bring with me before arriving at WeWork?",
    answer:
      "Please ensure you have proof of identification,and any necessary equipment you need to make the most of your visit. Oh, and don’t forget a charger :)",
  },
  {
    question: "Can I change the building I have access to?",
    answer:
      "Currently you may only access the building you reserved, in accordance to the number of entries you purchased. We are currently working on a multi-location access option, stay tuned!",
  },
  {
    question: "How do I book a day that I wish to receive access to?",
    answer: (
      <Text>
        Simply go to{" "}
        <TextLink href="https://partners.flexspace.ai/portal/wework">
          your dashboard
        </TextLink>{" "}
        and choose your desired date!
      </Text>
    ),
  },
  {
    question: "How do I modify or cancel my booking?",
    answer: (
      <Text>
        You may cancel or modify the date of your reservation 24 hours before
        your booking begins (until 9 AM the day before your reservation). In
        order to change your reservation, please reach out to
        <TextLink href="mailto:help@flexspace.ai">
          help@flexspace.ai
        </TextLink>{" "}
        24 hours before the reservation begins.
      </Text>
    ),
  },
  {
    question: "Why is the date I’m looking for not available?",
    answer:
      "In order to maximize the user experience, we are limiting the daily capacity for Daily Accesses. Feel free to check out other WeWork buildings/dates.",
  },
  {
    question: "What is your guest policy for desk bookings?",
    answer: (
      <Text>
        Guests are not permitted with daily desk bookings. If you'd like to
        bring a guest, please{" "}
        <TextLink href="/offerings/wework-meeting-rooms">
          book a conference room
        </TextLink>{" "}
        that can accommodate each of your guests.
      </Text>
    ),
  },
  {
    question: "Can I leave my equipment at WeWork if I have a Multi Pass?",
    answer:
      "Multi-Day Access users are required to take all personal belongings with them by the end of the work day.",
  },
  {
    question: "Do I have printer access?",
    answer: "Printing is not included under the Multi-Day Access membership.",
  },
];

export const faqsHE = [
  {
    question: "מה כוללת הגישה באמצעות הכרטיסיה?",
    answer:
      "כרטיסיית גישות יומיות מעניקה לכם אפשרות להיכנס למיקום הנבחר של WeWork במספר מועדים שונים אשר ישוריינו לפני ההגעה. הגישה תתאפשר בשעות הפעילות (09:00-17:00), בימים ראשון-חמישי. במסגרת הגישה היומית ניתן לעבוד מעמדות העבודה הפזורות בחללים המשותפים או באזורים משותפים אחרים במיקום שבחרתם.",
  },
  {
    question: "מה צריך לעשות כשמגיעים לבניין?",
    answer:
      "יש לבצע צ'ק אין כאשר אתם מגיעים לבניין, אנא גשו לצוות הקהילה שלנו בדלפק הכניסה בעת הגעתכם. לאחר מכן תקבלו כרטיס מפתח שיאפשר לכם גישה במהלך יום העבודה. רק אל תשכחו להביא איתכם תעודה מזהה, זה הדבר הראשון שצוות הקהילה יבקש מכם על מנת למסור לידכם כרטיס גישה.",
  },
  {
    question: "משהו ספציפי שצריך להביא לפני ההגעה ל-WeWork?",
    answer:
      "אנא וודאו שאתם מביאים איתכם תעודה מזהה, וכל ציוד אחר הדרוש לכם כדי להפיק את המירב מהביקור שלכם. אל תשכחו להביא מטען כמובן (:",
  },
  {
    question: "האם ניתן לשנות את הבניין אליו יש גישה?",
    answer:
      "נכון לעכשיו ניתן לקבל גישה רק לבניין אליו ביצעתם את ההזמנה, בהתאם למספר הכניסות שרכשתם. אנו עובדים בימים אלה על כרטיסייה שתאפשר כניסה למספר מיקומים שונים.",
  },
  {
    question: "איך משריינים יום עבודה?",
    answer: (
      <Text>
        פשוט נכנסים לעמוד{" "}
        <TextLink href="https://partners.flexspace.ai/portal/wework">
          ניהול ההזמנות
        </TextLink>{" "}
        ובוחרים את התאריך הרצוי.
      </Text>
    ),
  },
  {
    question: "איך ניתן לשנות או לבטל את ההזמנה שלי?",
    answer: (
      <Text>
        ניתן לבטל או לשנות את ההזמנה עד 24 שעות לפני תחילת ההזמנה שלכם (עד השעה
        09:00 ביום שלפני ההזמנה שלכם). על מנת לבטל את ההזמנה יש לשלוח בקשה
        לכתובת האימייל{" "}
        <TextLink href="mailto:help@flexspace.ai">help@flexspace.ai</TextLink>{" "}
        עד 24 שעות לפני תחילתה כאמור.
      </Text>
    ),
  },
  {
    question: "מדוע תאריך מסוים אינו זמין?",
    answer:
      "כדי לספק את החווייה הטובה ביותר אנחנו מגבילים את כמות הגישות היומיות הניתנות ללוקיישן ספציפי. אנחנו מזמינים אתכם לבדוק בניינים או תאריכים אחרים.",
  },
  {
    question: "מה אומרת המדיניות שלכם בנוגע להבאת אורחים?",
    answer: (
      <Text>
        אין להביא אורחים כאשר מזמינים גישה יומית. אם ברצונכם להזמין אורחים,{" "}
        <TextLink href="/offerings/wework-meeting-rooms">
          אנא שריינו חדר ישיבות
        </TextLink>{" "}
        המתאים למספר האורחים שתרצו להזמין. יש להזין את שמות האורחים לפני תחילת
        ההזמנה והגעתם לבניין.{" "}
      </Text>
    ),
  },
  {
    question: "האם ניתן להשאיר ציוד ב-WeWork אם יש לי מספר כניסות?",
    answer:
      "לא. על המשתמשים בכרטיסיית גישות יומיות לקחת את כל הציוד האישי שלהם איתם בסוף היום.",
  },
  {
    question: "האם יש אפשרות להשתמש במדפסת?",
    answer: "הדפסות אינן כלולות ב׳גישה יומית׳.",
  },
];

export const generalInfoEN: GeneralInformation = {
  title: "General information and cancellation policy",
  description:
    "Here are a few important details to note before booking your Multi-Day Access:",
  bulletPoints: [
    {
      point: "Cancellation and changes",
      text: (
        <Text>
          You may cancel or change the date of your reservation 24 hours prior
          to your booking (until 9 AM the day before your reservation).{" "}
          <TextLink href="https://partners.flexspace.ai/portal/wework">
            Click here to manage your reservations.
          </TextLink>
        </Text>
      ),
    },
    {
      point: "Access hours",
      text: "Access to the building will be available between 9 AM to 5 PM Sunday - Thursday. Please note that it is not possible to access the building before 9 AM.",
    },
    {
      point: "Validation",
      text: "Your Multi-Day Access is valid for 3 months starting the day of your purchase.",
    },
    {
      point: "Location",
      text: "The Multi-Day Access allows multiple entries to one specific building. We are currently working on a multi-location access option, stay tuned!",
    },
    {
      point: "Your work station",
      text: "The Multi-Day Access allows you to work from our open shared spaces and/or lounges at the chosen building. The specific location in the building can be changed based on occupancy availability.",
    },
    {
      point: "Guest policy",
      text: (
        <Text>
          Guests are not allowed with your Multi-Day Access booking. If you'd
          like to bring a guest, please
          <TextLink href="/offerings/wework-meeting-rooms">
            book a conference room
          </TextLink>{" "}
          that can accommodate the number of your guests. Guests' names must be
          entered before booking and arriving at the building.
        </Text>
      ),
    },
  ],
};

export const generalInfoHE: GeneralInformation = {
  title: "מידע כללי ומדיניות ביטולים",
  description:
    "לפניכם מידע שחשוב לדעת לפני שאתם מזמינים כרטיסיית גישות יומיות:",
  bulletPoints: [
    {
      point: "ביטולים ושינויים",
      text: (
        <Text>
          ניתן לשנות או לבטל את הזמנתכם עד 24 שעות לפני מועד ההזמנה (עד 9 בבוקר
          ביום שלפני מועד ההזמנה){" "}
          <TextLink href="https://partners.flexspace.ai/portal/wework">
            לחצו כאן לניהול ההזמנות שלכם.
          </TextLink>
        </Text>
      ),
    },
    {
      point: "שעות פעילות",
      text: "הגישה לבניין תתאפשר בין השעות 09:00 ל-17:00 בימים ראשון עד חמישי. כניסה לבניין לפני השעה 09:00 איננה אפשרית.",
    },
    {
      point: "תוקף הכרטיסייה",
      text: "תוקף כרטיסיית גישות יומיות הוא עד 3 חודשים מיום הרכישה.",
    },
    {
      point: "מיקום",
      text: "כרטיסיית גישות יומיות מאפשרת מספר כניסות למיקום ספציפי. אנו עובדים בימים אלה על כרטיסייה שתאפשר כניסה למספר מיקומים שונים.",
    },
    {
      point: "עמדת העבודה שלכם",
      text: "כרטיסיית גישה יומית מאפשרת לכם לעבוד מלאונג'ים וחללים פתוחים ומשותפים במיקום שבחרתם. מיקומים ספציפי בתוך הבניין יכול להשתנות בהתאם לתפוסת החללים והזמינות שלהם.",
    },
    {
      point: "מדיניות אורחים",
      text: (
        <Text>
          אין להביא אורחים כאשר מזמינים גישה יומית. אם ברצונכם להזמין אורחים,
          אנא שריינו
          <TextLink href="/offerings/wework-meeting-rooms">
            חדר ישיבות
          </TextLink>{" "}
          המתאים למספר האורחים שתרצו להזמין. יש להזין את שמות האורחים לפני תחילת
          ההזמנה והגעתם לבניין.
        </Text>
      ),
    },
  ],
};

export const descriptionEN = `WeWork Multi-Day Access provides you with a coworking space bundle, without a long term commitment. Choose the location and the number of daily access and enjoy a discounted daily price. Whether you’re tackling to-do’s or kicking off a project, you’ll have the space, community, and amenities to work towards your goals. Reserve your spots in advance, check in, and get to work.`;
export const descriptionHE = `כרטייסית גישות יומיות מאפשרת לכם להנות ממספר כניסות לחלל עבודה משותף ללא התחייבות לטווח ארוך. בחרו במיקום ובמספר הכניסות שאתם מעוניינים ותהנו ממחיר משתלם יותר. בין אם אתם צריכים מקום כדי להשלים משימות ובין אם מקום כדי להתחיל פרוייקט חדש, תוכלו ליהנות מחלל, מקהילה ומשירותים שיקרבו אתכם למטרה. אז איך זה עובד? מזמינים מקום מראש, מגיעים לצ'ק אין זריז ומתחילים לעבוד.`;

export function OfferingWeWorkMultiPass() {
  useWeWorkAnalytics();
  const dailyLocationGalleryFeatureFlag = useDailyLocationGalleryFeatureFlag();
  const partnerConfig = useWeWorkPartnerConfig();
  return (
    <Fragment>
      <Theme />
      <OfferingDetailsMultiPass
        showAddress
        showDirections
        showBackButton={dailyLocationGalleryFeatureFlag}
        partnerConfig={partnerConfig}
        data={{
          description: descriptionEN,
          faqs: faqsEN,
          generalInfo: generalInfoEN,
        }}
      />
    </Fragment>
  );
}
